/**
 * Analytics utility for tracking user actions
 * Provides functions to track button clicks, navigation, form submissions, etc.
 */

export type EventType =
  | 'button_click'
  | 'navigation'
  | 'form_submission'
  | 'page_view'
  | 'api_call'
  | 'error'
  | 'authentication'
  | 'other';

export interface TrackEventData {
  eventType: EventType;
  eventName: string;
  description?: string;
  pagePath: string;
  referrer?: string;
  formData?: Record<string, any>;
  metadata?: Record<string, any>;
}

/**
 * Track an event in the analytics system
 * Uses the main API instance which has proper auth and interceptors
 * @param data - Event data to track
 */
export const trackEvent = async (data: TrackEventData): Promise<void> => {
  try {
    // Import here to avoid circular dependencies
    const api = (await import('../api/index').then(m => m.default));
    
    const payload = {
      event_type: data.eventType,
      event_name: data.eventName,
      description: data.description || '',
      page_path: data.pagePath,
      referrer: data.referrer || window.location.pathname,
      form_data: data.formData || null,
      metadata: data.metadata || null,
    };

    const response = await api.post('/analytics/track', payload);

    console.log('Analytics event tracked:', data.eventName, response.data);
  } catch (error) {
    console.error('Failed to track analytics event:', error);
    // Don't throw error - analytics should not break user experience
  }
};

/**
 * Track a button click
 * @param buttonName - Name/label of the button
 * @param metadata - Optional metadata about the click
 */
export const trackButtonClick = (
  buttonName: string,
  metadata?: Record<string, any>
): void => {
  trackEvent({
    eventType: 'button_click',
    eventName: `clicked_${buttonName.toLowerCase().replace(/\s+/g, '_')}`,
    description: `User clicked on ${buttonName} button`,
    pagePath: window.location.pathname,
    metadata,
  });
};

/**
 * Track page/route navigation
 * @param fromPath - Previous path
 * @param toPath - New path
 * @param metadata - Optional metadata
 */
export const trackNavigation = (
  fromPath: string,
  toPath: string,
  metadata?: Record<string, any>
): void => {
  trackEvent({
    eventType: 'navigation',
    eventName: `navigation_${fromPath.replace(/\//g, '_')}_to_${toPath.replace(/\//g, '_')}`,
    description: `User navigated from ${fromPath} to ${toPath}`,
    pagePath: toPath,
    referrer: fromPath,
    metadata,
  });
};

/**
 * Track form submission
 * @param formName - Name of the form
 * @param formData - Form data being submitted
 * @param metadata - Optional metadata
 */
export const trackFormSubmission = (
  formName: string,
  formData: Record<string, any>,
  metadata?: Record<string, any>
): void => {
  // Sanitize sensitive form fields
  const sanitizedData = sanitizeFormData(formData);

  trackEvent({
    eventType: 'form_submission',
    eventName: `form_submitted_${formName.toLowerCase().replace(/\s+/g, '_')}`,
    description: `User submitted ${formName} form`,
    pagePath: window.location.pathname,
    formData: sanitizedData,
    metadata,
  });
};

/**
 * Track page view
 * @param pageName - Name/title of the page
 * @param pagePath - Path of the page
 */
export const trackPageView = (pageName: string, pagePath: string): void => {
  trackEvent({
    eventType: 'page_view',
    eventName: `page_viewed_${pageName.toLowerCase().replace(/\s+/g, '_')}`,
    description: `User viewed ${pageName} page`,
    pagePath,
    metadata: {
      page_name: pageName,
      timestamp: new Date().toISOString(),
    },
  });
};

/**
 * Track API calls
 * @param method - HTTP method
 * @param endpoint - API endpoint
 * @param status - HTTP status code
 * @param metadata - Optional metadata
 */
export const trackAPICall = (
  method: string,
  endpoint: string,
  status: number,
  metadata?: Record<string, any>
): void => {
  trackEvent({
    eventType: 'api_call',
    eventName: `api_${method.toLowerCase()}_${endpoint.replace(/\//g, '_')}`,
    description: `API ${method} ${endpoint} returned ${status}`,
    pagePath: window.location.pathname,
    metadata: {
      method,
      endpoint,
      status,
      ...metadata,
    },
  });
};

/**
 * Track errors
 * @param errorMessage - Error message
 * @param errorType - Type of error
 * @param metadata - Optional metadata
 */
export const trackError = (
  errorMessage: string,
  errorType: string = 'javascript_error',
  metadata?: Record<string, any>
): void => {
  trackEvent({
    eventType: 'error',
    eventName: `error_${errorType.toLowerCase().replace(/\s+/g, '_')}`,
    description: `Error: ${errorMessage}`,
    pagePath: window.location.pathname,
    metadata: {
      error_type: errorType,
      error_message: errorMessage,
      ...metadata,
    },
  });
};

/**
 * Track authentication events
 * @param action - Authentication action (login, logout, register, etc.)
 * @param metadata - Optional metadata
 */
export const trackAuthentication = (action: string, metadata?: Record<string, any>): void => {
  trackEvent({
    eventType: 'authentication',
    eventName: `auth_${action.toLowerCase()}`,
    description: `User ${action.toLowerCase()}`,
    pagePath: window.location.pathname,
    metadata,
  });
};

/**
 * Sanitize sensitive form data before sending to analytics
 * @param formData - Original form data
 * @returns Sanitized form data
 */
const sanitizeFormData = (formData: Record<string, any>): Record<string, any> => {
  const sanitized: Record<string, any> = {};
  const sensitiveFields = ['password', 'pin', 'cvv', 'card_number', 'secret', 'token'];

  for (const [key, value] of Object.entries(formData)) {
    const lowerKey = key.toLowerCase();
    const isSensitive = sensitiveFields.some(field => lowerKey.includes(field));

    if (isSensitive) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeFormData(value);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
};
